/**
 * Populates any element marked with `data-delivery-estimate` with a Spanish
 * delivery-window string computed from today + N business days (Mon–Fri).
 *
 * Business rule:
 *   - Carrier transit days are looked up from COUNTRY_DAYS keyed by the
 *     ISO 3166-1 alpha-2 code exposed via `window.IUROP_SHIP_COUNTRY` (set
 *     by theme.liquid from `localization.country.iso_code`).
 *   - The displayed window is [transit, transit + WINDOW_BUFFER] business
 *     days from today.
 *   - If the country has no service, the host element is hidden and a small
 *     fallback note can be rendered by data attributes.
 *
 * Per-block overrides (data attributes on the host element):
 *   data-delivery-min     → fallback min when country is unknown
 *   data-delivery-max     → fallback max when country is unknown
 *   data-delivery-fixed   → "true" ignores country lookup, uses data attrs
 *   data-delivery-prefix  → text inserted before the date (default " – llega")
 *
 * Output examples (default prefix):
 *   " – llega del 2 al 4 de abril"
 *   " – llega del 30 de marzo al 2 de abril"
 *   " – sin servicio de envío"
 */
(function () {
  'use strict';

  const T_SHIPPING = (window.theme && window.theme.t && window.theme.t.shipping) || {};
  const MONTHS_ES = (Array.isArray(T_SHIPPING.months) && T_SHIPPING.months.length === 12)
    ? T_SHIPPING.months
    : [
        'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
        'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
      ];

  // Delivery range in business days by ISO 3166-1 alpha-2 country code.
  // Values: [min, max] tuple, or single integer (max = value + WINDOW_BUFFER),
  // or `null` = sin servicio (estimate hidden for that country).
  //
  // Source: tarifas oficiales Susmies actualizadas (envío estándar por país).
  // For Spain, postal-code prefixes override this (see ES_CP_DAYS below).
  const COUNTRY_DAYS = {
    // España (Península 24-48h, Baleares/Canarias por CP)
    ES: [1, 2],

    // Portugal (estándar 3-5d)
    PT: [3, 5],

    // Europa Vecinos (Bélgica, Francia, Alemania, Italia, Países Bajos)
    // Estándar 5-7d. Express 2-3d disponible al cambiar de transportista.
    BE: [5, 7], FR: [5, 7], DE: [5, 7], IT: [5, 7], NL: [5, 7],

    // Europa (resto) — estándar 5-7d
    AD: [5, 7], AT: [5, 7], DK: [5, 7], FI: [5, 7], IE: [5, 7], LV: [5, 7],
    HU: [5, 7], LI: [5, 7], LU: [5, 7], PL: [5, 7], SK: [5, 7], NO: [5, 7],
    SE: [5, 7], AL: [5, 7], BA: [5, 7], VA: [5, 7], XK: [5, 7], ME: [5, 7],
    MK: [5, 7], SM: [5, 7], RS: [5, 7], MC: [5, 7],

    // Europa Este + Marruecos, Emiratos Árabes, Qatar y Sudáfrica (3-5d)
    BG: [3, 5], RO: [3, 5], CZ: [3, 5], EE: [3, 5], LT: [3, 5], HR: [3, 5],
    CY: [3, 5], MT: [3, 5], SI: [3, 5], GR: [3, 5], UA: [3, 5], MD: [3, 5],
    BY: [3, 5], RU: [3, 5],
    MA: [3, 5], AE: [3, 5], QA: [3, 5], ZA: [3, 5],

    // Reino Unido (4-6d)
    GB: [4, 6],

    // Suiza (3-5d)
    CH: [3, 5],

    // Estados Unidos (exprés 3-5d, almacén local)
    US: [3, 5],

    // Canadá (3-5d)
    CA: [3, 5],

    // Asia (5-7d)
    JP: [5, 7], KR: [5, 7], CN: [5, 7], HK: [5, 7], TW: [5, 7], MN: [5, 7],
    IN: [5, 7], PK: [5, 7], BD: [5, 7], LK: [5, 7], NP: [5, 7], BT: [5, 7],
    MV: [5, 7], MM: [5, 7], TH: [5, 7], VN: [5, 7], KH: [5, 7], LA: [5, 7],
    MY: [5, 7], SG: [5, 7], ID: [5, 7], PH: [5, 7], BN: [5, 7], TL: [5, 7],
    KZ: [5, 7], KG: [5, 7], UZ: [5, 7], TJ: [5, 7], TM: [5, 7],
    MO: [5, 7],

    // Oceanía — Australia, Nueva Zelanda y resto (5-8d)
    AU: [5, 8], NZ: [5, 8], FJ: [5, 8], PG: [5, 8], NC: [5, 8], PF: [5, 8],
    WS: [5, 8], AS: [5, 8], TO: [5, 8], VU: [5, 8], KI: [5, 8], NR: [5, 8],
    NU: [5, 8], PW: [5, 8], FM: [5, 8], MH: [5, 8], SB: [5, 8], MP: [5, 8],
    GU: [5, 8], TV: [5, 8], CK: [5, 8],

    // Sudamérica + Caribe (5-7d)
    MX: [5, 7], PR: [5, 7], GT: [5, 7], BZ: [5, 7], SV: [5, 7], HN: [5, 7],
    NI: [5, 7], CR: [5, 7], PA: [5, 7],
    CO: [5, 7], VE: [5, 7], GY: [5, 7], SR: [5, 7], BR: [5, 7], EC: [5, 7],
    PE: [5, 7], BO: [5, 7], PY: [5, 7], CL: [5, 7], AR: [5, 7], UY: [5, 7],
    BS: [5, 7], CU: [5, 7], DO: [5, 7], HT: [5, 7], JM: [5, 7], TT: [5, 7],
    BB: [5, 7], AG: [5, 7], DM: [5, 7], GD: [5, 7], KN: [5, 7], LC: [5, 7],
    VC: [5, 7], AI: [5, 7], KY: [5, 7], TC: [5, 7], VG: [5, 7], VI: [5, 7],
    BM: [5, 7], MS: [5, 7], AW: [5, 7], BQ: [5, 7], CW: [5, 7], SX: [5, 7],
    BL: [5, 7], MF: [5, 7], GP: [5, 7], MQ: [5, 7], GF: [5, 7],

    // Islandia (3-5d)
    IS: [3, 5],

    // Israel — tarifas y plazos calculados en checkout: ocultar estimate
    IL: null,

    // Sin servicio
    AF: null, KP: null,
  };

  const DEFAULT_MIN = 2;
  const DEFAULT_MAX = 4;
  const WINDOW_BUFFER = 2; // max = transit + WINDOW_BUFFER
  const CP_STORAGE_KEY = 'iurop_ship_cp';

  // Spanish provinces by 2-digit postal-code prefix → carrier transit days.
  // Source: PDF "Tiempos carrier - Nacional".
  // Tuple [min, max] when the province has its own range (Canarias).
  const ES_CP_DAYS = {
    '01': 1, '02': 1, '03': 1, '04': 2, '05': 2, '06': 2, '07': 2, '08': 1,
    '09': 2, '10': 2, '11': 2, '12': 1, '13': 2, '14': 2, '15': 2, '16': 1,
    '17': 1, '18': 2, '19': 1, '20': 1, '21': 2, '22': 1, '23': 2, '24': 2,
    '25': 1, '26': 1, '27': 2, '28': 1, '29': 2, '30': 1, '31': 1, '32': 2,
    '33': 2, '34': 2, '35': [4, 7], '36': 2, '37': 2, '38': [4, 7], '39': 2,
    '40': 2, '41': 2, '42': 2, '43': 1, '44': 1, '45': 2, '46': 1, '47': 2,
    '48': 1, '49': 2, '50': 1, '51': 3, '52': 3,
  };

  const getCountry = () => {
    const c = (window.IUROP_SHIP_COUNTRY || '').toString().trim().toUpperCase();
    return c.length === 2 ? c : '';
  };

  const getStoredCp = () => {
    try {
      const cp = (localStorage.getItem(CP_STORAGE_KEY) || '').trim();
      return /^\d{5}$/.test(cp) ? cp : '';
    } catch (e) {
      return '';
    }
  };

  const setStoredCp = (cp) => {
    try {
      if (cp) localStorage.setItem(CP_STORAGE_KEY, cp);
      else localStorage.removeItem(CP_STORAGE_KEY);
    } catch (e) {
      /* ignore */
    }
  };

  const cpToRange = (cp) => {
    if (!/^\d{5}$/.test(cp)) return null;
    const prefix = cp.slice(0, 2);
    const entry = ES_CP_DAYS[prefix];
    if (entry === undefined) return null;
    if (Array.isArray(entry)) return { min: entry[0], max: entry[1] };
    return { min: entry, max: entry + WINDOW_BUFFER };
  };

  const resolveRange = (el) => {
    const fixed = el.dataset.deliveryFixed === 'true';
    const country = fixed ? '' : getCountry();

    if (country === 'ES' && !fixed) {
      const cpRange = cpToRange(getStoredCp());
      if (cpRange) return cpRange;
    }

    if (country && Object.prototype.hasOwnProperty.call(COUNTRY_DAYS, country)) {
      const entry = COUNTRY_DAYS[country];
      if (entry === null) return null; // sin servicio
      if (Array.isArray(entry)) return { min: entry[0], max: entry[1] };
      return { min: entry, max: entry + WINDOW_BUFFER };
    }

    return {
      min: parseInt(el.dataset.deliveryMin, 10) || DEFAULT_MIN,
      max: parseInt(el.dataset.deliveryMax, 10) || DEFAULT_MAX,
    };
  };

  const addBusinessDays = (date, days) => {
    const result = new Date(date);
    let added = 0;
    while (added < days) {
      result.setDate(result.getDate() + 1);
      const dow = result.getDay();
      if (dow !== 0 && dow !== 6) added += 1;
    }
    return result;
  };

  const formatRange = (start, end, prefix) => {
    const startDay = start.getDate();
    const endDay = end.getDate();
    const startMonth = MONTHS_ES[start.getMonth()];
    const endMonth = MONTHS_ES[end.getMonth()];

    if (start.getMonth() === end.getMonth()) {
      const tpl = T_SHIPPING.rangeSameMonth || 'del [start] al [end] de [month]';
      return `${prefix} ${tpl.replace('[start]', startDay).replace('[end]', endDay).replace('[month]', endMonth)}`;
    }
    const tplDiff = T_SHIPPING.rangeDiffMonth || 'del [start] de [start_month] al [end] de [end_month]';
    return `${prefix} ${tplDiff.replace('[start]', startDay).replace('[start_month]', startMonth).replace('[end]', endDay).replace('[end_month]', endMonth)}`;
  };

  const getPrefix = (el) => {
    const raw = el.dataset.deliveryPrefix;
    if (raw !== undefined) return raw;
    return T_SHIPPING.deliveryPrefix || ' – llega';
  };

  const paintOne = (el) => {
    const range = resolveRange(el);
    const prefix = getPrefix(el);
    if (!range) {
      const noService = T_SHIPPING.noService || ' sin servicio de envío';
      el.textContent = `${prefix || ''}${noService}`.replace(/^\s+/, ' ');
      el.hidden = false;
      return;
    }
    const today = new Date();
    const start = addBusinessDays(today, range.min);
    const end = addBusinessDays(today, range.max);
    el.textContent = range.min === range.max
      ? formatRange(start, start, prefix)
      : formatRange(start, end, prefix);
    el.hidden = false;
  };

  const paintAll = (root = document) => {
    root.querySelectorAll('[data-delivery-estimate]').forEach(paintOne);
    syncCpUi(root);
  };

  // ---- Postal-code form (sidecart only) -------------------------------------

  const syncCpUi = (root = document) => {
    const country = getCountry();
    const cp = getStoredCp();
    root.querySelectorAll('[data-cp-form]').forEach((form) => {
      // Hide entirely outside Spain — CP only adjusts Spanish provinces.
      form.hidden = country !== 'ES';
    });
    root.querySelectorAll('[data-cp-current]').forEach((el) => {
      el.hidden = !cp;
      const valueEl = el.querySelector('[data-cp-current-value]');
      if (valueEl) valueEl.textContent = cp;
    });
    root.querySelectorAll('[data-cp-toggle]').forEach((btn) => {
      btn.hidden = !!cp;
    });
  };

  const expandCpInput = (form) => {
    form.classList.add('is-open');
    const input = form.querySelector('[data-cp-input]');
    if (input) {
      input.value = getStoredCp() || '';
      input.focus();
    }
  };

  const collapseCpInput = (form) => {
    form.classList.remove('is-open');
    const error = form.querySelector('[data-cp-error]');
    if (error) error.hidden = true;
  };

  const submitCp = (form) => {
    const input = form.querySelector('[data-cp-input]');
    const error = form.querySelector('[data-cp-error]');
    const value = (input?.value || '').trim();
    if (!cpToRange(value)) {
      if (error) {
        const tCart = (window.theme && window.theme.t && window.theme.t.cart) || {};
        error.textContent = tCart.cpInvalid || 'Código postal no válido';
        error.hidden = false;
      }
      input?.focus();
      return;
    }
    setStoredCp(value);
    collapseCpInput(form);
    paintAll();
  };

  const clearCp = () => {
    setStoredCp('');
    paintAll();
  };

  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-cp-toggle]');
    if (toggle) {
      e.preventDefault();
      const form = toggle.closest('[data-cp-form]');
      if (form) expandCpInput(form);
      return;
    }
    const submit = e.target.closest('[data-cp-submit]');
    if (submit) {
      e.preventDefault();
      const form = submit.closest('[data-cp-form]');
      if (form) submitCp(form);
      return;
    }
    const clear = e.target.closest('[data-cp-clear]');
    if (clear) {
      e.preventDefault();
      clearCp();
      return;
    }
    const cancel = e.target.closest('[data-cp-cancel]');
    if (cancel) {
      e.preventDefault();
      const form = cancel.closest('[data-cp-form]');
      if (form) collapseCpInput(form);
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter') return;
    const input = e.target.closest?.('[data-cp-input]');
    if (!input) return;
    e.preventDefault();
    const form = input.closest('[data-cp-form]');
    if (form) submitCp(form);
  });

  // ---- Boot -----------------------------------------------------------------

  const init = () => paintAll();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run whenever the cart drawer (or any other section) re-renders.
  document.addEventListener('cart:updated', () => paintAll());
  document.addEventListener('shopify:section:load', (e) => paintAll(e.target));

  // Re-run when the customer changes country in the localization form.
  document.addEventListener('localization:country-changed', () => paintAll());

  // Safety net: observe the body for [data-delivery-estimate] elements added
  // dynamically (e.g. cart drawer fragment swaps) and paint them on arrival.
  const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      m.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.matches?.('[data-delivery-estimate]')) paintOne(node);
        node.querySelectorAll?.('[data-delivery-estimate]').forEach(paintOne);
        if (node.matches?.('[data-cp-form]') || node.querySelector?.('[data-cp-form]')) {
          syncCpUi(node);
        }
      });
    }
  });
  mo.observe(document.body, { childList: true, subtree: true });
})();
